<script setup lang="ts">
import { ref, reactive } from 'vue';
import { toast } from 'vue3-toastify';
import dartsHubApi from '@/axios/dartsHubApi';
import ConfirmModal from '@/components/ConfirmModal.vue';
import { useConfirm } from '@/composables/useConfirm';

const { open: openConfirm } = useConfirm();

interface CleanupTool {
  id: string;
  title: string;
  description: string;
  endpoint: string;
  readOnly?: boolean;
  // Outil qui reçoit un fichier (jamais conservé côté serveur) et une saison "AAAA-AAAA".
  fileAccept?: string;
  // Plusieurs fichiers de la même saison en un envoi (fusionnés côté serveur).
  multipleFiles?: boolean;
  needsSeason?: boolean;
}

const TOOLS: CleanupTool[] = [
  {
    id: 'initialize-org-history',
    title: "Initialiser l'historique des clubs, comités et ligues",
    description: "À lancer une fois, avec le PDF fédéral « Clubs FFD 2025-2026 ». Reconstitue les fiches 2025-2026 (numéro, ligue et comité du fichier ; nom et sigle repris de la base, corrigés depuis) complétées par les licences de la saison, et 2024-2025 d'après les licences. Puis crée les fiches de la saison en cours, recopiées ensuite chaque nuit (Admin > Historique). Un club du fichier inconnu de la base est créé sans affiliation ; un numéro porté en base par un autre club est signalé, sans rien rattacher. Une saison qui a déjà des fiches n'est jamais retouchée : relançable sans risque.",
    endpoint: '/org-history/initialize',
    fileAccept: '.pdf',
  },
  {
    id: 'reorganize-aquitaine',
    title: 'Réorganiser la ligue Aquitaine (2026-2027)',
    description: "Supprime le comité provisoire « Corse & Outre-mer » de la ligue FFD et Corsica Darts Club, historique compris (la ligue FFD reste pour la Réunion ; les fiches 2025-2026 des clubs réunionnais restent sans comité). Passe AQE dans le bloc 42 (n° fédéral 42000) : LFC 42001000, Wolfpack 42002000, Triple 19 42003000, Skol 42004000, SMTC 42005000, Bort Artense 42006000 ; licences de la saison en cours, factures et demandes suivent, les licences passées gardent leur numéro. Rattache les départements (AQN 16, 17, 79, 86 ; AQS 24, 33, 47). Crée sans sigle les clubs manquants : Darts Club Marchois 42007000, L'Abeille Auventaise 42008000, Les Fléchettes Folles 42009000, Glandarts 42010000, et avec un numéro provisoire négatif Montendarts, Saint Varent DC (AQN), Saint-Médard-en-Jalles DC, Artiguaise DC, Dart'Agen Templar (AQS). Limousin Darts Club : fiche 2024-2025 en AQN (affilié) et disparu sur la saison en cours. À lancer APRÈS l'initialisation de l'historique (refusé sinon). Relançable sans risque : ce qui est déjà fait est ignoré, ce qui ne colle pas est signalé en conflit.",
    endpoint: '/admin/cleanup/reorganize-aquitaine',
  },
  {
    id: 'import-historical-licenses',
    title: "Importer les licences d'une saison passée (fichier comité)",
    description: "Reconstitue l'historique des licences depuis un fichier comité au format FFD (xlsx : LIG, COM, N° Club, NOM, Prénom, Licence, Cat., Club, adresse, C.P., VILLE, téléphones, E-mail, naissance). Une licence déjà en base pour ce numéro et cette saison n'est jamais modifiée. La base prime pour l'identité (nom, prénom, date de naissance). Le club se déduit du numéro de licence, avec le nom de club de l'époque ; un club renuméroté depuis est retrouvé par son historique, un club disparu est recréé sans affiliation. Crée aussi la fiche d'historique de la saison des clubs qui y ont des licences. Un numéro en double dans le fichier reprend celui que la base connaît. Importer les saisons dans l'ordre chronologique : la licence précédente se renseigne d'après la saison d'avant. Plusieurs fichiers de la même saison (comités, mises à jour MAJ n°1 / n°2) peuvent être envoyés ensemble : une même licence n'est comptée qu'une fois (ligne du fichier dont le nom vient en dernier).",
    endpoint: '/admin/cleanup/import-historical-licenses',
    fileAccept: '.xlsx,.xls',
    multipleFiles: true,
    needsSeason: true,
  },
  {
    id: 'link-previous-licenses',
    title: 'Relier chaque licence à la licence précédente du joueur',
    description: "Pour chaque licence sans « licence précédente », retrouve la dernière licence de la même personne sur une saison antérieure (même compte relié, à défaut même nom, prénom et date de naissance), une saison blanche au plus comme pour un renouvellement : renseigne n°, club et saison précédents (Renouvellement si même numéro, Transfert sinon). Ne remplit que des champs vides. Saisons passées seulement : les licences de la saison en cours concernées sont listées en conflit sans être modifiées (code FFD NL/RL/MO et facturation). À lancer après les imports de saisons passées.",
    endpoint: '/licenses/admin/link-previous-licenses',
  },
  {
    id: 'backfill-license-submission-dates',
    title: 'Dater la soumission au comité des licences SDE (saison en cours)',
    description: "Les licences validées avant l'enregistrement de la date de soumission au comité (colonne ajoutée le 15/09/2026) n'en ont aucune : l'import de classement retombe alors sur la date de validation, et un joueur soumis avant un tournoi mais validé après sort à tort non licencié (cas Hélène Juez, Open de Ligue #1 de Cournon). Reprend la dernière soumission au comité du registre précédant la validation, à défaut la date de création de la facture club (une facture n'est créée qu'à partir de demandes déjà soumises). Ne touche jamais une licence déjà datée. Après application, réimporter les classements concernés : le statut licencié est figé à l'import.",
    endpoint: '/admin/cleanup/backfill-license-submission-dates',
  },
  {
    id: 'backfill-payment-refs',
    title: 'Retrouver les codes des paiements HelloAsso passés',
    description: "Le code ajouté au nom de l'article HelloAsso (« Inscription : Open de Comité #3 #A9A747 ») n'est enregistré sur la tentative d'inscription que depuis le 19/10/2026. Le retrouve dans les logs serveur, qui gardent chaque paiement lancé, et le rapproche exactement de sa tentative (même jeton d'inscription). Les logs ne remontent qu'à 30 jours. Ne remplace jamais un code déjà renseigné. Un paiement lancé avant la première tentative en base (avant le suivi des inscriptions, 16/10/2026) n'a rien à quoi se rattacher : il est seulement compté (skipped_before_tracking), pas listé en conflit. Un paiement plus récent sans tentative reste un conflit.",
    endpoint: '/admin/cleanup/backfill-payment-refs',
  },
  {
    id: 'backfill-registration-source',
    title: "Reconstituer le site d'inscription (registered_via)",
    description: "Pour les comptes encore en origine \"DartsHub\" (créés avant le suivi du site d'inscription, ou directement sur dartshub.fr sans passer par un site) : combine en un seul passage la preuve directe du registre (première connexion sur un site connu après l'inscription, même des jours plus tard ; une connexion sur dartshub.fr lui-même ne compte pas), la déduction par club de licence (CMER ne gère que le comité MER, donc un club hors MER ne peut pas venir de CMER, ce qui ne laisse que LSEF), puis une liste de comptes reconnus à la main pour les derniers cas. Corrige aussi le registre en synchro. Depuis le correctif, un compte en \"DartsHub\" prend automatiquement l'origine du premier site connu qui le connecte : cet outil ne sert plus qu'à rattraper l'historique.",
    endpoint: '/admin/cleanup/backfill-registration-source',
  },
  {
    id: 'fix-double-validations',
    title: 'Réparer les licences validées deux fois',
    description: "Une facture marquée payée deux fois pendant que la validation automatique tournait encore, ou une demande saisie en double puis validée en lot, a validé deux fois la même demande. Deux dégâts possibles : une licence créée en double dans la même facture (Montuelle Julien, #35014009 et #35014010), la plus récente est supprimée ; ou une licence transformée en renouvellement d'elle-même (Paquier Sébastien, Chabbert Loïc), sa licence précédente est remise à vide si la demande d'origine était un « Nouveau licencié » dans le registre, sinon signalée en conflit à trancher à la main. Ne corrige pas un export Ligue/FFD déjà transmis : à signaler au comité.",
    endpoint: '/admin/cleanup/fix-double-validations',
  },
  {
    id: 'resync-user-links',
    title: 'Resynchroniser les liens compte <-> licence',
    description: "user_id (la référence fiable) et user_email (un simple affichage, censé rester synchronisé) peuvent diverger : soit le compte est bien relié mais son email affiché est faux ou vide (ex. Mauran Noha, Martin Thomas), soit l'email affiché est juste mais le lien user_id n'a jamais été posé. Corrige les deux cas sur les licences et les demandes en cours. Délie aussi une demande reliée au compte d'un homonyme (même nom, autre date de naissance que les licences du compte, ex. Julien Faure né en 1977 relié au compte de Julien Faure né en 1976) et recalcule son renouvellement/transfert ; jamais un nouveau lien vers un homonyme. Un compte relié à des licences de dates de naissance différentes est seulement signalé en conflit.",
    endpoint: '/licenses/admin/resync-user-links',
  },
  {
    id: 'audit-account-name-mismatch',
    title: "Auditer les licences liées à un compte au nom différent",
    description: "Repère les licences/demandes dont le compte lié (user_id) porte un nom qui ne correspond pas du tout au titulaire, y compris les cas isolés (un seul lien sous ce compte). Un nom différent n'est pas toujours une erreur (compte mal nommé) : lecture seule, à trancher au cas par cas. Cas vécu : la licence de Mauran Noha reliée par erreur au compte superadmin.",
    endpoint: '/licenses/admin/audit-account-name-mismatch',
    readOnly: true,
  },
  {
    id: 'resync-previous-license-flags',
    title: "Resynchroniser \"licencié l'an dernier\" / club précédent",
    description: "Recalcule \"licencié l'an dernier\" des licences d'après la dernière saison jouée : oui seulement si c'est la saison juste avant, non après une saison blanche (ex. Vacher, Charlot : 2024-2025 puis 2026-2027). Sur les demandes en attente, le passe à \"oui\" s'il est resté à \"non\" alors qu'une licence antérieure est bien retrouvée (ex. Robert Vincent). Corrige aussi le club précédent vide OU périmé : un club renommé depuis (casse, orthographe) laisse une valeur non vide mais qui ne correspond plus à aucune option du formulaire, donc affichée comme non renseignée (ex. David Boos, \"Vojvodarts Côte D'azur\" vs \"Côte d'Azur\" aujourd'hui). Concerne les licences déjà validées et les demandes encore en attente.",
    endpoint: '/licenses/admin/resync-previous-license-flags',
  },
  {
    id: 'cleanup-postal-codes',
    title: 'Corriger les codes postaux (0 initial perdu, ".0", espaces)',
    description: "Corrige les codes postaux abîmés par un import Excel qui a lu la colonne comme un nombre (0 initial perdu, ex. \"3200\" -> \"03200\" ; \".0\" ajouté, ex. \"63800.0\" -> \"63800\") ou portant des espaces parasites (\"69009 \"), sur les licences et demandes en cours. Ne touche pas aux codes étrangers (ex. \"4854ET\"). Les imports et formulaires normalisent désormais le code postal à l'entrée.",
    endpoint: '/licenses/admin/cleanup-postal-codes',
  },
  {
    id: 'normalize-inscription-unlicensed-labels',
    title: 'Normaliser le club des inscriptions (« Non licencié », suffixe de comité)',
    description: "Le club d'un inscrit non licencié était écrit de cinq façons selon la voie d'inscription ou d'import (« Non licencié », « Non-licencié », « Non Licencié », « NL », « Non renseigné ») et seules certaines étaient reconnues comme non licencié : un « NL » ou un « Non-licencié » passait pour licencié (montant dû, exports Dartup). Ramène toutes ces variantes à « Non licencié ». Retire aussi le suffixe de comité laissé par d'anciens imports de fichier (« La Seyne Darts Club (MER) » → « La Seyne Darts Club »), qui empêchait de retrouver le code du club dans les exports logiciels (Darts4Windows...). Les nouvelles inscriptions sont déjà normalisées à l'écriture : cet outil ne reprend que l'existant.",
    endpoint: '/admin/cleanup/normalize-inscription-unlicensed-labels',
  },
  {
    id: 'clear-organizer-registrant-email',
    title: "Retirer l'organisateur des « Mes inscriptions » (ajouts manuels et imports)",
    description: "L'ajout manuel d'un joueur, les imports HelloAsso/FFD et Excel et l'inscription rapide depuis l'import de classement enregistraient l'organisateur comme auteur de l'inscription : elle apparaissait dans ses propres « Mes inscriptions » et son tableau de bord (ex. Thierry Pingeon, réinscrit à la main sur la Coupe de la Ligue). Corrigé à l'écriture le 31/10/2026 ; cet outil reprend l'existant : inscriptions sans jeton d'inscription en ligne, créées après la reprise des données du 22/05/2026 (dont l'email est celui du joueur, conservé). Qui a fait l'ajout reste visible sur le paiement.",
    endpoint: '/admin/cleanup/clear-organizer-registrant-email',
  },
  {
    id: 'backfill-event-organizer-club',
    title: "Rattacher club organisateur/encaisseur et refermer les inscriptions non configurées",
    description: "Les événements officiels ET les tournois locaux créés avant l'ajout du système premium/inscriptions n'ont que le nom du club organisateur en texte libre (club_name), sans les liens organizer_club_id (autorise la gestion/le premium) ni premium_owner_type/id (détermine qui gère/encaisse, condition pour que les onglets Inscriptions/Pointage et \"S'inscrire\" s'affichent) désormais utilisés -- un club qui active son propre premium ne voit rien débloqué sur ses tournois locaux existants tant que ce backfill n'a pas tourné. Rattache automatiquement les deux (gestionnaire = club organisateur par défaut) pour les événements dont le nom de club correspond exactement à un club existant (cas ambigus en conflit, à traiter à la main), et referme registration_status sur \"closed\" pour tout événement affiché comme ouvert alors que son entité gestionnaire n'a en réalité aucune config HelloAsso valide.",
    endpoint: '/admin/cleanup/backfill-event-organizer-club',
  },
];

const results = reactive<Record<string, any>>({});
const loading = reactive<Record<string, boolean>>({});
const previewed = reactive<Record<string, boolean>>({});
const selectedFiles = reactive<Record<string, File[]>>({});
const seasons = reactive<Record<string, string>>({});

function onFileChange(tool: CleanupTool, event: Event) {
  selectedFiles[tool.id] = Array.from((event.target as HTMLInputElement).files || []);
  previewed[tool.id] = false;
  results[tool.id] = null;
}

function isReady(tool: CleanupTool): boolean {
  if (tool.fileAccept && !selectedFiles[tool.id]?.length) return false;
  if (tool.needsSeason && !/^\d{4}-\d{4}$/.test(seasons[tool.id] || '')) return false;
  return true;
}

function summaryStats(result: any): { label: string; value: number }[] {
  if (!result) return [];
  return Object.entries(result)
    .filter(([k, v]) => k.endsWith('_count') && typeof v === 'number')
    .map(([k, v]) => ({ label: k.replace(/_count$/, '').replace(/_/g, ' '), value: v as number }));
}

function totalChanges(result: any): number {
  return summaryStats(result).reduce((sum, s) => sum + s.value, 0);
}

function detailRows(result: any): any[] {
  if (!result) return [];
  let rows: any[] = [];
  for (const key of ['changes', 'licenses_fixed', 'requests_fixed']) {
    if (Array.isArray(result[key]) && result[key].length) {
      rows = result[key];
      break;
    }
  }
  if (Array.isArray(result.conflicts) && result.conflicts.length) {
    rows = [...rows, ...result.conflicts.map((c: any) => ({ ...c, _conflict: true }))];
  }
  // Cas connus, rien à trancher (ex. homonymes à date de naissance différente) : affichés
  // pour information, jamais comptés comme changements ni conflits.
  if (Array.isArray(result.homonyms_set_aside) && result.homonyms_set_aside.length) {
    rows = [...rows, ...result.homonyms_set_aside.map((c: any) => ({ ...c, _setAside: true }))];
  }
  return rows;
}

function rowLabel(row: Record<string, any>): string {
  if (row.type === 'event_organizer_link') {
    return `Événement #${row.id} "${row.display_name || row.name}" : club organisateur rattaché à "${row.club_name}"`;
  }
  if (row.type === 'event_premium_owner_link') {
    return `Événement #${row.id} "${row.display_name || row.name}" : gestionnaire/encaisseur rattaché au club "${row.club_name}"`;
  }
  if (row.type === 'event_registration_closed') {
    return `Événement #${row.id} "${row.display_name || row.name}" : inscriptions refermées ("${row.before}" -> "closed", entité gestionnaire non configurée)`;
  }
  if (row.submitted_to_committee_at) {
    const when = new Date(`${row.submitted_to_committee_at}Z`).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' });
    return `Licence #${row.id} ${row.name} ${row.firstname} (${row.club}) : soumise le ${when} [${row.method}]`;
  }
  if (row._setAside) {
    const who = [row.firstname, row.name].filter(Boolean).join(' ');
    return `Écarté : ${row.table} #${row.id}${who ? ` (${who})` : ''} : ${row.reason}`;
  }
  if (row._conflict) {
    const ids = row.ids ? ` (id: ${row.ids.join(', ')})` : row.id !== undefined ? ` (id ${row.id})` : '';
    const subject = row.email || row.display_name || row.name || '';
    const table = row.table ? `${row.table}${subject ? ` "${subject}"` : ''}${ids} : ` : '';
    // Conflits de l'import des licences historiques : joueur et numéro, sans table.
    const who = !row.table && (row.name || row.license_number)
      ? `${row.name || ''}${row.license_number ? ` #${row.license_number}` : ''} : `
      : '';
    return `⚠ Conflit non résolu : ${table}${who}${row.reason}`;
  }

  if (row.table && row.field) {
    const who = [row.firstname, row.name].filter(Boolean).join(' ');
    return `${row.table} #${row.id}${who ? ` (${who})` : ''} : ${row.field} "${row.before}" -> "${row.after}"`;
  }

  const parts = [];
  if (row.field) parts.push(row.field);
  if (row.before !== undefined) parts.push(`"${row.before}" -> "${row.after}"`);
  if (row.name) parts.push(`${row.name} ${row.firstname || ''}`.trim());
  if (row.license_number) parts.push(`#${row.license_number}`);
  if (row.reason) parts.push(row.reason);
  return parts.join(' | ') || JSON.stringify(row);
}

async function postCleanup(tool: CleanupTool, dryRun: boolean) {
  const params = new URLSearchParams({ dry_run: String(dryRun) });
  if (tool.needsSeason) params.set('season', seasons[tool.id] || '');
  const url = `${tool.endpoint}?${params.toString()}`;
  const chosen = selectedFiles[tool.id] || [];
  if (tool.fileAccept && chosen.length) {
    const form = new FormData();
    if (tool.multipleFiles) chosen.forEach(f => form.append('files', f));
    else form.append('file', chosen[0]);
    const { data } = await dartsHubApi.post(url, form);
    return data;
  }
  const { data } = await dartsHubApi.post(url);
  return data;
}

const preview = async (tool: CleanupTool) => {
  loading[tool.id] = true;
  try {
    results[tool.id] = await postCleanup(tool, true);
    previewed[tool.id] = true;
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur lors de la prévisualisation.');
  } finally {
    loading[tool.id] = false;
  }
};

const apply = async (tool: CleanupTool) => {
  const n = totalChanges(results[tool.id]);
  const ok = await openConfirm(
    'Appliquer ce nettoyage ?',
    `${n} changement${n > 1 ? 's' : ''} vont être écrits en base. Cette action n'est pas automatiquement réversible.`,
  );
  if (!ok) return;
  loading[tool.id] = true;
  try {
    results[tool.id] = await postCleanup(tool, false);
    toast.success('Nettoyage appliqué.');
  } catch (err: any) {
    toast.error(err.response?.data?.detail || "Erreur lors de l'application.");
  } finally {
    loading[tool.id] = false;
  }
};
</script>

<template>
  <div class="page">
    <ConfirmModal />
    <div class="page-header">
      <h1><i class="fa-solid fa-broom" /> Nettoyage des données</h1>
    </div>
    <p class="page-hint">
      Chaque outil fonctionne en deux temps : "Prévisualiser" (aucune écriture) puis "Appliquer" une fois le résultat vérifié.
    </p>

    <div class="tool-list">
      <div v-for="tool in TOOLS" class="tool-card" :key="tool.id">
        <div class="tool-header">
          <div>
            <div class="tool-title">{{ tool.title }}</div>
            <div class="tool-desc">{{ tool.description }}</div>
          </div>
          <div class="tool-actions">
            <button class="btn btn-ghost btn-sm" :disabled="loading[tool.id] || !isReady(tool)" @click="preview(tool)">
              <i v-if="loading[tool.id]" class="fa-solid fa-spinner fa-spin" />
              <i v-else class="fa-solid fa-magnifying-glass" />
              {{ tool.readOnly ? 'Analyser' : 'Prévisualiser' }}
            </button>
            <button
              v-if="!tool.readOnly"
              class="btn btn-primary btn-sm"
              :disabled="!previewed[tool.id] || loading[tool.id] || totalChanges(results[tool.id]) === 0"
              @click="apply(tool)"
            >
              <i class="fa-solid fa-check" /> Appliquer
            </button>
          </div>
        </div>

        <div v-if="tool.fileAccept || tool.needsSeason" class="tool-inputs">
          <input
            v-if="tool.needsSeason"
            v-model="seasons[tool.id]"
            class="season-input"
            type="text"
            placeholder="Saison (AAAA-AAAA)"
            @input="previewed[tool.id] = false"
          />
          <label v-if="tool.fileAccept" class="file-label">
            <i class="fa-solid fa-file-arrow-up" />
            {{ selectedFiles[tool.id]?.length
              ? selectedFiles[tool.id].map(f => f.name).join(', ')
              : tool.multipleFiles ? `Choisir un ou plusieurs fichiers de la saison (${tool.fileAccept})` : `Choisir le fichier (${tool.fileAccept})` }}
            <input type="file" :accept="tool.fileAccept" :multiple="tool.multipleFiles" hidden @change="onFileChange(tool, $event)" />
          </label>
        </div>

        <div v-if="results[tool.id]" class="tool-result">
          <div class="stat-row">
            <span v-for="s in summaryStats(results[tool.id])" class="badge badge-green" :key="s.label">
              {{ s.value }} {{ s.label }}
            </span>
            <span v-if="!summaryStats(results[tool.id]).length" class="badge badge-gray">
              Aucun changement détecté
            </span>
          </div>
          <div v-if="detailRows(results[tool.id]).length" class="detail-list">
            <div v-for="(row, i) in detailRows(results[tool.id]).slice(0, 100)" class="detail-row" :key="i">
              {{ rowLabel(row) }}
            </div>
            <div v-if="detailRows(results[tool.id]).length > 100" class="detail-more">
              + {{ detailRows(results[tool.id]).length - 100 }} autre(s), non affiché(s)
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-hint {
  color: var(--text-muted);
  font-size: 0.85rem;
  margin: -0.5rem 0 1.25rem;
}

.tool-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.tool-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1rem 1.25rem;
}

.tool-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.tool-title {
  font-weight: 600;
  font-size: 0.95rem;
}

.tool-inputs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.season-input {
  padding: 0.35rem 0.6rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--bg-card);
  color: inherit;
  font-size: 0.85rem;
  width: 11rem;
}

.file-label {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.75rem;
  border: 1px dashed var(--border);
  border-radius: var(--radius);
  font-size: 0.85rem;
  cursor: pointer;
}

.file-label:hover {
  border-color: var(--text-muted);
}

.tool-desc {
  color: var(--text-muted);
  font-size: 0.82rem;
  margin-top: 0.25rem;
  max-width: 60ch;
}

.tool-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.tool-result {
  margin-top: 0.9rem;
  padding-top: 0.9rem;
  border-top: 1px solid var(--border);
}

.stat-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.detail-list {
  margin-top: 0.6rem;
  max-height: 260px;
  overflow-y: auto;
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
}

.detail-row {
  font-size: 0.78rem;
  font-family: ui-monospace, "Cascadia Code", "SF Mono", Consolas, monospace;
  padding: 3px 0;
  border-bottom: 1px solid var(--border);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-more {
  font-size: 0.75rem;
  color: var(--text-muted);
  padding-top: 4px;
  text-align: center;
}
</style>
